import { computed, ReadonlySignal, Signal, signal } from '@preact/signals'

import {
  Camera,
  CameraAccess,
  CameraPosition,
  CameraSettings,
  DeviceCamera,
  FrameSourceState,
  VideoResolution,
} from '@scandit/web-datacapture-core'

import type AppStore from './AppStore'

const ResolutionLabel = {
  [VideoResolution.Auto]: '',
  [VideoResolution.HD]: '',
  [VideoResolution.FullHD]: '(Full HD)',
  [VideoResolution.UHD4K]: '(4K)',
}

class CameraOption {
  public name: string
  public id: `${Camera['deviceId']}%${VideoResolution}`
  public camera: Camera
  public wantedResolution: VideoResolution
  public disabled: Signal<boolean>

  constructor(camera: Camera, wantedResolution = VideoResolution.HD) {
    this.camera = camera
    this.id = `${camera.deviceId}%${wantedResolution}`
    this.name = `${ResolutionLabel[wantedResolution]} ${camera.label}`
    this.wantedResolution = wantedResolution
    this.disabled = signal(false)
  }

  get deviceId() {
    return this.camera.deviceId
  }
}

type PossibleUnsupportedVideoResolutions = Exclude<VideoResolution, VideoResolution.Auto | VideoResolution.HD>

const UnsupportedMessagesResolutionMessages: Record<PossibleUnsupportedVideoResolutions, string> = {
  [VideoResolution.FullHD]:
    "The selected camera doesn't seem to support Full HD resolution, HD resolution will be used as fallback.",
  [VideoResolution.UHD4K]:
    "The selected camera doesn't seem to support 4K resolution, FullHD or HD resolution will be used as fallback.",
}

export type TCameraStore = {
  state: Signal<FrameSourceState.On | FrameSourceState.Off>
  hasFetchedCameras: ReadonlySignal<boolean>
  availableCameras: Signal<CameraOption[]>
  selectedCamera: Signal<CameraOption | undefined>
  fetchCameras: () => Promise<void>
  changeCamera: (cameraOption: CameraOption) => Promise<void>
  setCameraSettings: (cameraSettings: CameraSettings) => void
  selectDefaultCamera: () => Promise<void>
}

const CameraStore = (appStore: AppStore): TCameraStore => {
  const state = signal<FrameSourceState.Off | FrameSourceState.On>(FrameSourceState.Off)
  const availableCameraOptions = signal<CameraOption[]>([])
  const hasFetchedCameras = computed(() => availableCameraOptions.value?.length > 0)
  const currentCameraOption = signal<CameraOption | undefined>(undefined)

  let cameraSettings = new CameraSettings({ preferredResolution: VideoResolution.FullHD } as CameraSettings)

  async function fetchCameras(): Promise<void> {
    if (hasFetchedCameras.value) return
    try {
      const parsedOptionsURL = new URLSearchParams(window.location.search)
      if (parsedOptionsURL.has('no-camera')) {
        const err = new Error('NoCameraAvailableError')
        err.name = 'NoCameraAvailableError'
        throw err
      }

      if (parsedOptionsURL.has('fullhd')) {
        setCameraSettings(new CameraSettings({ preferredResolution: VideoResolution.FullHD } as CameraSettings))
      }

      const tempDeviceCameras: Camera[] = (await CameraAccess.getCameras(true, false)).map((camera: DeviceCamera) =>
        Camera.fromDeviceCamera(camera)
      )

      const worldFacing = [],
            userFacing = []
      for (const camera of tempDeviceCameras) {
        if (camera.position === CameraPosition.WorldFacing) {
          worldFacing.push(camera)
        } else if (camera.position === CameraPosition.UserFacing) {
          userFacing.push(camera)
        }
      }
      // We're sorting the cameras here cause in the XCover6 Pro the back cameras are not
      // correctly sorted: camera2 2 is before camera2 0.
      // This result in ultra wide camera selection in the aforementioned device.
      const deviceCameras = [
        ...worldFacing.sort((camera1, camera2) => camera1.label.localeCompare(camera2.label)),
        ...userFacing.sort((camera1, camera2) => camera1.label.localeCompare(camera2.label)),
      ]

      availableCameraOptions.value = deviceCameras.reduce((newCameras: CameraOption[], camera) => {
        return newCameras.concat([
          new CameraOption(camera, VideoResolution.HD),
          new CameraOption(camera, VideoResolution.FullHD),
          new CameraOption(camera, VideoResolution.UHD4K),
        ])
      }, [])
      state.value = FrameSourceState.On
    } catch (error: any) {
      appStore.errorsStore.handleError(error)
    }
  }

  const changeCamera = async (cameraOption: CameraOption) => {
    currentCameraOption.value = cameraOption

    await getCurrentCamera()?.switchToDesiredState(FrameSourceState.Off)
    await setCurrentCamera(currentCameraOption.value)
    await getCurrentCamera()?.switchToDesiredState(FrameSourceState.On)

    const fallbackResolution = await fallbackUnsupportedCameraResolution()
    if (fallbackResolution) {
      const fallbackOption = availableCameraOptions.value.find((option) => {
        return option.wantedResolution === fallbackResolution && option.deviceId === cameraOption.deviceId
      })

      // Mark option as disabled
      cameraOption.disabled.value = true

      if (fallbackOption) {
        await changeCamera(fallbackOption)
      }
      if (UnsupportedMessagesResolutionMessages[cameraOption.wantedResolution as PossibleUnsupportedVideoResolutions])
        alert(
          UnsupportedMessagesResolutionMessages[cameraOption.wantedResolution as PossibleUnsupportedVideoResolutions]
        )
    }
  }

  const getCurrentCamera = (): Camera | null => {
    const { context } = appStore.sdk!
    return context?.frameSource as Camera
  }

  const setCurrentCamera = async (cameraOption: CameraOption) => {
    const { context } = appStore.sdk!
    cameraSettings.preferredResolution = cameraOption.wantedResolution
    await cameraOption.camera.applySettings(cameraSettings)
    return context?.setFrameSource(cameraOption.camera)
  }

  const fallbackUnsupportedCameraResolution = async (): Promise<
    VideoResolution.HD | VideoResolution.FullHD | undefined
  > => {
    const camera = await getCurrentCamera()
    if (!camera) return undefined
    const supportResolution = checkResolutionsSupport(camera)

    if (!supportResolution[VideoResolution.FullHD] && cameraSettings.preferredResolution === VideoResolution.FullHD) {
      // fallback to hd
      return VideoResolution.HD
    }

    if (!supportResolution[VideoResolution.UHD4K] && cameraSettings.preferredResolution === VideoResolution.UHD4K) {
      // fallback to fullhd
      return VideoResolution.FullHD
    }
  }

  const selectDefaultCamera = async () => {
    // Default camera has device id and label empty until is accessed
    const defaultCamera = Camera.default;
    // we create a mock option with the default camera and access it to populate the device id and label
    await changeCamera(new CameraOption(defaultCamera, VideoResolution.FullHD))
    // we find the option with the same label as the default camera so the ui displays the correct camera selected
    currentCameraOption.value = availableCameraOptions.value.find((option) => option.camera.label === defaultCamera.label)
  }

  const setCameraSettings = (newCameraSettings: CameraSettings) => (cameraSettings = newCameraSettings)

  return {
    state,
    hasFetchedCameras,
    availableCameras: availableCameraOptions,
    selectedCamera: currentCameraOption,
    fetchCameras,
    changeCamera,
    setCameraSettings,
    selectDefaultCamera,
  }
}

const checkResolutionsSupport = (
  camera: Camera
): { [VideoResolution.UHD4K]: boolean; [VideoResolution.FullHD]: boolean } => {
  const { width = 0, height = 0 } = camera.currentResolution ?? {}
  return {
    [VideoResolution.FullHD]: width >= 1080 || height >= 1080,
    [VideoResolution.UHD4K]: width >= 2160 || height >= 2160,
  }
}

export default CameraStore
